const DESKTOP_MQ = '(min-width: 990px)';

class CuratedBundlesCarousel extends HTMLElement {
  /** @type {number} */
  #index = 0;

  /** @type {number} */
  #slideCount = 0;

  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;

  /** @type {boolean} */
  #loopCloned = false;

  /** @type {MediaQueryList | null} */
  #desktopMq = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('.sc-bundle-banner__slide:not(.sc-bundle-banner__slide--clone)'));
    this.#dots = Array.from(this.querySelectorAll('.sc-bundle-banner__dot'));
    this.#track = this.querySelector('[ref="track"]');
    this.#viewport = this.querySelector('[ref="viewport"]');
    this.#mediaTrack = this.querySelector('[ref="mediaTrack"]');
    this.#productsTrack = this.querySelector('[ref="productsTrack"]');
    this.#mediaViewport = this.querySelector('[ref="mediaViewport"]');
    this.#productsViewport = this.querySelector('[ref="productsViewport"]');
    this.#copySlides = Array.from(this.querySelectorAll('.sc-bundle-banner__copy-slide'));
    this.#ctaSlides = Array.from(this.querySelectorAll('.sc-bundle-banner__cta-slide'));
    this.#slideCount = this.#slides.length;

    if (this.#slideCount <= 1) return;

    this.#desktopMq = window.matchMedia(DESKTOP_MQ);
    this.#desktopMq.addEventListener('change', this.#onResize);

    if (!this.#isDesktop()) {
      this.#setupLoopClone();
    }

    this.#dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.index);
        if (!Number.isNaN(index)) this.#goTo(index);
      });
    });

    this.#viewport?.addEventListener('pointerdown', (e) => this.#onPointerDown(e));
    this.#viewport?.addEventListener('pointermove', (e) => this.#onPointerMove(e));
    this.#viewport?.addEventListener('pointerup', () => this.#onPointerUp());
    this.#viewport?.addEventListener('pointercancel', () => this.#onPointerUp());
    this.#viewport?.addEventListener('lostpointercapture', () => this.#onPointerUp());

    this.addEventListener('mouseenter', () => this.#stopAutoplay());
    this.addEventListener('mouseleave', () => this.#startAutoplay());
    this.#startAutoplay();

    this.#apply();
    this.#updateUi();
    window.addEventListener('resize', this.#onResize, { passive: true });
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#desktopMq?.removeEventListener('change', this.#onResize);
    window.removeEventListener('resize', this.#onResize);
  }

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement | null} */
  #mediaTrack = null;

  /** @type {HTMLElement | null} */
  #productsTrack = null;

  /** @type {HTMLElement | null} */
  #mediaViewport = null;

  /** @type {HTMLElement | null} */
  #productsViewport = null;

  /** @type {HTMLElement[]} */
  #copySlides = [];

  /** @type {HTMLAnchorElement[]} */
  #ctaSlides = [];

  /** @type {number | null} */
  #pointerId = null;

  /** @type {number} */
  #startX = 0;

  /** @type {number} */
  #deltaX = 0;

  /** @type {boolean} */
  #dragging = false;

  #isDesktop() {
    return this.#desktopMq?.matches ?? window.matchMedia(DESKTOP_MQ).matches;
  }

  #onResize = () => {
    if (this.#isDesktop() && this.#loopCloned) {
      this.#track?.querySelector('.sc-bundle-banner__slide--clone')?.remove();
      this.#loopCloned = false;
      this.#index = this.#index % this.#slideCount;
    } else if (!this.#isDesktop()) {
      this.#setupLoopClone();
    }

    this.#apply(true);
    this.#updateUi();
  };

  #setupLoopClone() {
    if (!this.#track || this.#loopCloned || this.#slideCount <= 1 || this.#isDesktop()) return;

    const clone = this.#slides[0].cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;

    clone.classList.add('sc-bundle-banner__slide--clone');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    this.#track.appendChild(clone);
    this.#loopCloned = true;
  }

  /**
   * @param {number} targetIndex
   */
  #goTo(targetIndex) {
    if (this.#slideCount === 0) return;

    const index = ((targetIndex % this.#slideCount) + this.#slideCount) % this.#slideCount;
    if (index === this.#index % this.#slideCount) return;

    this.#index = index;
    this.#apply();
    this.#updateUi();
  }

  #goNext() {
    if (this.#slideCount === 0) return;

    if (this.#isDesktop()) {
      this.#index = (this.#index + 1) % this.#slideCount;
      this.#apply();
      this.#updateUi();
      return;
    }

    if (!this.#track) return;

    this.#index += 1;
    this.#apply();
    this.#updateUi();

    if (this.#index === this.#slideCount) {
      const onEnd = () => {
        this.#track?.removeEventListener('transitionend', onEnd);
        this.#index = 0;
        this.#apply(true);
        this.#updateUi();
      };
      this.#track.addEventListener('transitionend', onEnd);
    }
  }

  #goPrev() {
    if (this.#slideCount === 0) return;

    if (this.#isDesktop()) {
      this.#index = (this.#index - 1 + this.#slideCount) % this.#slideCount;
      this.#apply();
      this.#updateUi();
      return;
    }

    if (!this.#track) return;

    if (this.#index === 0 && this.#loopCloned) {
      this.#index = this.#slideCount;
      this.#apply(true);

      requestAnimationFrame(() => {
        this.#index = this.#slideCount - 1;
        this.#apply();
        this.#updateUi();
      });
      return;
    }

    this.#index = Math.max(0, this.#index - 1);
    this.#apply();
    this.#updateUi();
  }

  /**
   * @returns {number}
   */
  #slideWidth() {
    return this.#viewport?.clientWidth || 1;
  }

  /**
   * @param {boolean} [noTransition]
   */
  #applyDesktop(noTransition = false) {
    const activeIndex = ((this.#index % this.#slideCount) + this.#slideCount) % this.#slideCount;
    const tracks = [
      { track: this.#mediaTrack, viewport: this.#mediaViewport },
      { track: this.#productsTrack, viewport: this.#productsViewport },
    ];

    for (const { track, viewport } of tracks) {
      if (!track || !viewport) continue;

      const offset = activeIndex * viewport.clientWidth;
      if (noTransition) track.style.transition = 'none';
      track.style.transform = `translate3d(-${offset}px, 0, 0)`;
      if (noTransition) {
        void track.offsetHeight;
        track.style.transition = '';
      }
    }

    this.#copySlides.forEach((slide, i) => {
      const active = i === activeIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  /**
   * @param {boolean} [noTransition]
   */
  #applyMobile(noTransition = false) {
    if (!this.#track) return;
    const offset = this.#index * this.#slideWidth();

    if (noTransition) this.#track.style.transition = 'none';
    this.#track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    if (noTransition) {
      void this.#track.offsetHeight;
      this.#track.style.transition = '';
    }
  }

  /**
   * @param {boolean} [noTransition]
   */
  #apply(noTransition = false) {
    if (this.#isDesktop()) {
      this.#applyDesktop(noTransition);
      return;
    }

    this.#applyMobile(noTransition);
  }

  #updateUi() {
    const activeIndex = ((this.#index % this.#slideCount) + this.#slideCount) % this.#slideCount;

    this.#slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === activeIndex ? 'false' : 'true');
    });

    this.#dots.forEach((dot) => {
      const dotIndex = Number(dot.dataset.index);
      if (Number.isNaN(dotIndex)) return;

      const active = dotIndex === activeIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.#ctaSlides.forEach((cta, i) => {
      const ctaIndex = i % this.#slideCount;
      const active = ctaIndex === activeIndex;
      cta.classList.toggle('is-active', active);
      cta.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  /**
   * @param {PointerEvent} e
   */
  #onPointerDown(e) {
    if (this.#isDesktop()) return;
    if (!this.#viewport || !this.#track) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('a, button, input, textarea, select, label')) return;

    this.#pointerId = e.pointerId;
    this.#viewport.setPointerCapture(e.pointerId);
    this.#startX = e.clientX;
    this.#deltaX = 0;
    this.#dragging = true;
    this.#stopAutoplay();

    this.#track.style.transition = 'none';
  }

  /**
   * @param {PointerEvent} e
   */
  #onPointerMove(e) {
    if (this.#isDesktop()) return;
    if (!this.#dragging || this.#pointerId !== e.pointerId) return;
    if (!this.#viewport || !this.#track) return;

    this.#deltaX = e.clientX - this.#startX;
    const width = this.#slideWidth();
    const visualIndex = this.#deltaX > 0 && this.#index === 0 && this.#loopCloned ? this.#slideCount : this.#index;
    const base = visualIndex * width;
    this.#track.style.transform = `translate3d(-${base - this.#deltaX}px, 0, 0)`;
  }

  #onPointerUp() {
    if (this.#isDesktop()) return;
    if (!this.#dragging) return;
    this.#dragging = false;
    this.#pointerId = null;

    if (!this.#viewport || !this.#track) return;
    const width = this.#slideWidth();
    const threshold = Math.min(90, width * 0.18);

    this.#track.style.transition = '';

    if (this.#deltaX < -threshold) {
      this.#goNext();
    } else if (this.#deltaX > threshold) {
      this.#goPrev();
    } else {
      this.#apply();
    }

    this.#deltaX = 0;
    this.#startAutoplay();
  }

  #startAutoplay() {
    const speed = Number(this.getAttribute('autoplay'));
    if (!speed || this.#slideCount <= 1) return;

    this.#stopAutoplay();
    this.#timer = setInterval(() => this.#goNext(), speed * 1000);
  }

  #stopAutoplay() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('curated-bundles-carousel')) {
  customElements.define('curated-bundles-carousel', CuratedBundlesCarousel);
}
